/**
 * Live client for the **wave 2** socket — the computed columns, from `qf-scr`.
 *
 * The third screener socket, and the smallest. {@link ScreenerRosterClient}
 * subscribes to a *roster* and gets wave 1 back; this one registers a **column
 * set** and gets wave 2 pushed to it. They carry the same named-frame payload
 * and the consumer merges both by field name, so there is no second decoder and
 * no second merge rule — only a second sender.
 *
 * It replaces a 30-second HTTP poll (`GET /scan/columns`). The poll could only
 * ever be as fresh as its last tick and paid a full request per tick whether or
 * not anything had changed; the socket lets the compute tier push the instant a
 * tick finishes.
 *
 * ## The protocol
 *
 * ```
 * client -> server   TEXT   {"type":"auth","token":…,"actAs":…}
 * client -> server   TEXT   {"type":"subscribe","groups":["RU2000"],
 *                            "mode":"union","computed":[…],"userId":…}
 * server -> client   BINARY the named-frame payload `GET /scan/columns`
 *                           already returns — detail · push · columns · timings
 * ```
 *
 * **The auth frame goes first and it is not optional plumbing.** A browser
 * cannot set `Authorization` on a WebSocket handshake, so the server accepts the
 * socket *before* authenticating it — the auth frame can only arrive on an
 * accepted socket — and reads the token off the opening message.
 *
 * ## The set belongs to the connection, and is always sent whole
 *
 * Every `subscribe` **replaces** this connection's set. There is no add, no
 * remove and no diff, so a reconnect is one idempotent message rather than a
 * resume the server would have to remember a dead connection to honour. That is
 * the settled design in `docs/plans/scan-freshness-and-push.md`: the compute
 * tier's union is "at least one connection wants it", which needs no reference
 * count and collects an orphaned identity on disconnect.
 *
 * An **empty** `computed` is therefore sent rather than omitted — the opposite
 * of {@link ScreenerRosterClient}, where an empty list would split one channel
 * key into two. Here the list *is* the message: a user who deletes their last
 * computed column has to be able to say so, or the tier keeps computing columns
 * nobody is looking at until the tab closes.
 *
 * ## An absent wave 2 is ordinary, not an error
 *
 * A screen with no computed columns is what shipped before `qf-scr` existed, so
 * a socket that will not open is a screen missing a column rather than a broken
 * one: a drop reports status, retries on the same backoff the other sockets use,
 * and emits **no frames at all**. A push that does not carry a column must not
 * clear it, and a disconnect carries no columns — so nothing on screen is
 * allowed to change because this socket died.
 */

import { decodeScreenerFrames, type ScreenerFrame } from "./screener-arrow.js";
import type {
  ScreenerComputedRequest,
  ScreenerSealedConditionRequest,
} from "./screener-client.js";
import type {
  ConnectionStatus,
  TransportError,
  WebSocketFactory,
  WebSocketLike,
} from "../chart/client.js";

/** The column set one connection owns: a universe, and what to compute over it. */
export interface ScanColumnSet {
  /** Group codes — stored groups, track lists, journal buckets. */
  codes: readonly string[];
  /** How to combine more than one code. */
  mode: "union" | "intersect";
  /**
   * The computed columns wanted, exactly as `serverComputedColumns` produces
   * them. May be empty: see the header — an empty set is a statement, not a
   * missing one.
   */
  computed: readonly ScreenerComputedRequest[];
  /**
   * Stored columns this screen shows that wave 1 marked `compute` — today the
   * Impulse (JAS) columns while the market is open. Named so the server sends
   * only what the screen shows. Omitted is none.
   */
  live?: readonly string[];
  /**
   * Ticked sealed conditions (`docs/plans/screen-links.md`). This tier answers
   * the ones whose fields it holds — the session's compute columns — and wave 1
   * the rest; the client reads `row[field]` from the merge either way.
   */
  sealedConditions?: readonly ScreenerSealedConditionRequest[];
  /**
   * A JP trade-list screen (`docs/plans/jp-recs.md`): the server adds the
   * inputs as computed columns and answers with `rec*` columns. Omitted for an
   * ordinary screen; dropped by the server for a caller without `ind:JP`.
   */
  recs?: { kind: string; frequency: string };
  /**
   * Who is asking, when the socket carries no usable token.
   *
   * **Without this a track list resolves to nobody's list.** Membership is
   * per-caller, so the server's roster resolution returns 0 symbols for a list
   * code with no caller, and an *intersect* containing one then collapses to an
   * empty roster. The HTTP route this replaces sent the same value as a query
   * parameter for the same reason.
   */
  userId?: string;
}

export interface ScanColumnsListeners {
  /** A payload arrived — immediately on subscribe, and on every push after it. */
  onFrames?: (frames: ScreenerFrame[]) => void;
  onStatus?: (status: ConnectionStatus) => void;
  onError?: (error: TransportError) => void;
}

export interface ScanColumnsClientOptions {
  /** Full socket URL, e.g. `"wss://qf-scr.fly.dev/scan/ws"`. */
  url: string;
  listeners?: ScanColumnsListeners;
  /** Inject a WebSocket implementation (defaults to the global `WebSocket`). */
  webSocketFactory?: WebSocketFactory;
  /**
   * The Firebase ID token to open with. Called on **every** open, including a
   * reconnect — a token expires in an hour, and a socket that drops overnight
   * would otherwise reconnect with a dead one and be closed immediately,
   * forever. Injected rather than imported for D18's reason: this package is
   * vendor-agnostic and is hosted in four WebViews.
   */
  authToken?: () => string | null | Promise<string | null>;
  /** Who the columns are *for*, when that differs from who is asking. */
  actAs?: () => string | null | undefined;
  /**
   * The signed groups pass from `/auth/me`, sent in the same opening frame.
   *
   * Lets the server decide what this caller may see without asking its
   * database (`docs/plans/auth-hot-path.md`). Optional: without it the server
   * resolves the caller from Postgres, exactly as before.
   */
  pass?: () => string | null | undefined;
}

// ── Reconnect schedule ──────────────────────────────────────────────────────
//
// The same numbers as `ScreenerRosterClient`, and copied for its stated reason:
// the delays are justified by a server's cadence, and folding three clients into
// one constant would make a change for the screener silently a change here.

/** First retry delay; doubles per consecutive failure. */
const RECONNECT_BASE_MS = 500;

/** Delay ceiling — past this the columns are stale enough that the user knows. */
const RECONNECT_CAP_MS = 30_000;

/**
 * How long a socket must *stay* open before the backoff counts it as a success.
 *
 * **`onopen` is not success on this protocol**, and resetting the counter there
 * is the bug that shape invites. The server accepts the socket before it can
 * read the auth frame — there is nowhere else the token could arrive — so
 * "opened, then closed by the server" is the ordinary shape of a rejected
 * token: an hours-old tab whose Firebase token expired, or `qf-scr` closing
 * after accept as its machine suspends. Counting the open as a success makes
 * every one of those attempt 0 again, so the doubling below and
 * {@link RECONNECT_CAP_MS} are unreachable and the browser reconnects every
 * ~375ms for as long as the tab is open — silently, because an absent wave 2 is
 * ordinary and nothing on screen reports it.
 */
const RECONNECT_PROVEN_MS = 5_000;

/**
 * How long to wait before retry number `attempt` (0-based).
 *
 * Jittered subtractively — `[d/2, d]` rather than `[0, d]` — so a qf-scr restart
 * does not march every open browser back onto it in the same millisecond, and so
 * the schedule never produces a near-instant retry onto a server that is still
 * starting up.
 */
function reconnectDelay(attempt: number): number {
  const ceiling = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_CAP_MS);
  return ceiling / 2 + Math.random() * (ceiling / 2);
}

function defaultWebSocketFactory(url: string): WebSocketLike {
  const Ctor = (globalThis as { WebSocket?: unknown }).WebSocket;
  if (typeof Ctor !== "function") {
    throw new Error("No global WebSocket available; pass options.webSocketFactory.");
  }
  return new (Ctor as new (u: string) => WebSocketLike)(url);
}

export class ScanColumnsClient {
  private readonly options: ScanColumnsClientOptions;
  private ws: WebSocketLike | null = null;
  private _status: ConnectionStatus = "idle";

  /**
   * The set in force — resent whole on every reconnect, and the flag that says
   * a reconnect is wanted at all.
   *
   * The server holds the set against the *connection* and remembers nothing
   * across a drop, so a reconnected socket that does not resend it is open,
   * healthy-looking and silent forever. That bug has now cost two of this app's
   * sockets a session each; it is not being repeated a third time.
   */
  private columns: ScanColumnSet | null = null;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;

  /** Pending while the current socket has not yet lived {@link RECONNECT_PROVEN_MS}. */
  private provenTimer: ReturnType<typeof setTimeout> | null = null;

  /** True from `disconnect()` until the next `connect()`; suppresses retries. */
  private closedByCaller = false;

  constructor(options: ScanColumnsClientOptions) {
    this.options = options;
  }

  get status(): ConnectionStatus {
    return this._status;
  }

  connect(): Promise<void> {
    if (this.ws && (this._status === "open" || this._status === "connecting")) {
      return Promise.resolve();
    }
    // A caller reconnecting by hand supersedes whatever we had scheduled, and
    // starts the backoff over: this attempt is not the failed one's successor.
    this.closedByCaller = false;
    this.reconnectAttempt = 0;
    this.cancelReconnect();
    return this.openSocket();
  }

  /**
   * Register a column set — on the first one, and on every change after it.
   *
   * **Not a reconnect, and never a diff.** A changed set is one text frame on
   * the socket that is already open, and it replaces what this connection asked
   * for. Recorded before it is sent, so a socket that dies mid-send resumes into
   * the set the caller asked for rather than the one before it, and so a call
   * made while the socket is still handshaking is honoured by `onopen` instead
   * of dropped.
   */
  subscribe(columns: ScanColumnSet): void {
    this.columns = columns;
    this.send(columns);
  }

  /**
   * Re-send the set in force — what the Refresh button does.
   *
   * The server answers from the frame it already holds, so this means *ask
   * again* rather than *recompute*: worth knowing before reading a refreshed
   * number as a recomputed one.
   */
  resubscribe(): void {
    if (this.columns) this.send(this.columns);
  }

  disconnect(): void {
    // Both of these run **before** `close()`, because a `WebSocketLike` may
    // deliver `onclose` synchronously and that handler must already know the
    // close was ours. A reconnect timer that outlives a disconnect resurrects a
    // client its owner has thrown away, and `disconnect()` runs on every unmount.
    this.closedByCaller = true;
    this.cancelReconnect();
    this.cancelProven();

    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      ws.close();
    }
    this.columns = null;
    this.setStatus("closed");
  }

  // ── internals ─────────────────────────────────────────────────────────────

  private send(columns: ScanColumnSet): void {
    if (!this.ws || this._status !== "open") return;
    const { codes, mode, computed, live, sealedConditions, recs, userId } = columns;
    this.ws.send(
      JSON.stringify({
        type: "subscribe",
        // `groups`, not `codes`: the name the HTTP route this replaces parses,
        // on the same service and for the same value. The roster socket's
        // `codes` is a different server.
        groups: codes,
        mode,
        // **Always sent, empty included** — the set is owned by the connection
        // and replaced wholesale, so an omitted list would leave the compute
        // tier holding columns the user has just deleted. See the header.
        //
        // `rollup` is dropped: it tells wave 1 how to aggregate a column into a
        // rollup row, and this tier computes values only. The poll sent the same
        // two fields.
        computed: computed.map((c) => ({ field: c.field, expression: c.expression })),
        // The server's name for them is `jas`, the only kind there is today.
        ...(live && live.length ? { jas: [...live] } : {}),
        // Omitted when empty, like `jas`: a subscribe replaces the whole set,
        // so absent is "none" here rather than "unchanged".
        ...(sealedConditions && sealedConditions.length ? { sealedConditions } : {}),
        // Omitted for an ordinary screen, `jas`'s rule.
        ...(recs ? { recs } : {}),
        ...(userId ? { userId } : {}),
      }),
    );
  }

  /** Open one socket and wire its handlers; shared by `connect` and the retry. */
  private openSocket(): Promise<void> {
    this.setStatus("connecting");

    const factory = this.options.webSocketFactory ?? defaultWebSocketFactory;
    const ws = factory(this.options.url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    return new Promise<void>((resolve, reject) => {
      ws.onopen = () => {
        // **The auth frame goes first, before the set.** The server accepts the
        // socket before authenticating it — there is nowhere else the token
        // could arrive — and closes it if the first message is not this one.
        void (async () => {
          try {
            const token = await this.options.authToken?.();
            // **Only the socket we are still holding may speak.** Minting a
            // token is a dynamic import plus, near expiry, a network refresh —
            // hundreds of milliseconds, which is longer than the first backoff
            // slot. So this continuation can resume after its own socket died
            // and was replaced, and every line below it would then act on the
            // replacement: announce "open" for a socket still handshaking, and
            // reach `send` on a CONNECTING one, which throws `InvalidStateError`
            // into a promise nobody is holding.
            if (this.ws !== ws) return;
            if (token) {
              const actAs = this.options.actAs?.() || undefined;
              const pass = this.options.pass?.() || undefined;
              ws.send(
                JSON.stringify({
                  type: "auth",
                  token,
                  ...(actAs ? { actAs } : {}),
                  ...(pass ? { pass } : {}),
                }),
              );
            }
          } catch {
            // A token we could not mint is the same as none: the server closes
            // the socket and the reconnect path handles it. Throwing here would
            // take out `onopen` and leave the socket half-alive.
          }
          // The catch above resumes here too, so the guard is repeated rather
          // than assumed: a failed mint on a superseded socket is the same
          // hazard as a slow one.
          if (this.ws !== ws) return;
          // Not `reconnectAttempt = 0` — see `RECONNECT_PROVEN_MS`. An open the
          // server immediately closes must leave the counter where it was.
          this.armProven();
          this.setStatus("open");
          resolve();
          // A socket that opens with a set already recorded is a replacement for
          // one that dropped, and nobody else will resend it — the consumer
          // wired a frames callback and never hears about the close at all.
          if (this.columns) this.send(this.columns);
        })();
      };
      ws.onmessage = (ev) => {
        this.handleMessage(ev.data);
      };
      ws.onclose = () => {
        // Only the socket we are still holding may declare the feed dead. A
        // superseded one closing — a `disconnect()` mid-handshake, or a browser
        // delivering the close of a socket already replaced — would otherwise
        // null out the live socket and stop the retries with it.
        if (this.ws !== ws) return;
        this.ws = null;
        // It did not live long enough to be proved good, so this failure is the
        // previous one's successor and the delay keeps doubling.
        this.cancelProven();
        this.setStatus("closed");
        this.scheduleReconnect();
      };
      ws.onerror = (cause) => {
        this.emitError({ source: "socket", message: "WebSocket error", cause });
        if (this._status === "connecting") reject(new Error("WebSocket error"));
      };
    });
  }

  /**
   * Wait out the backoff, then try again.
   *
   * Guarded on a timer already being pending because `onclose` and a throwing
   * factory can both reach here for one failure, and two live timers would halve
   * the delay every time they raced.
   */
  private scheduleReconnect(): void {
    if (this.closedByCaller || !this.columns) return;
    if (this.reconnectTimer !== null) return;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closedByCaller) return;
      try {
        void this.openSocket().catch(() => {
          // A refused handshake announces itself through `onerror` and then
          // `onclose`, and the close schedules the next attempt. This catch
          // exists only so the rejection is not an unhandled one.
        });
      } catch (cause) {
        // The factory itself threw, so there is no socket to deliver an
        // `onclose` and the retry chain would end here, silently.
        this.emitError({ source: "socket", message: "reconnect failed", cause });
        this.scheduleReconnect();
      }
    }, reconnectDelay(this.reconnectAttempt++));
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** Start counting this socket's life; surviving the wait clears the backoff. */
  private armProven(): void {
    this.cancelProven();
    this.provenTimer = setTimeout(() => {
      this.provenTimer = null;
      this.reconnectAttempt = 0;
    }, RECONNECT_PROVEN_MS);
  }

  private cancelProven(): void {
    if (this.provenTimer !== null) {
      clearTimeout(this.provenTimer);
      this.provenTimer = null;
    }
  }

  private handleMessage(data: unknown): void {
    let bytes: Uint8Array;
    if (data instanceof ArrayBuffer) bytes = new Uint8Array(data);
    else if (data instanceof Uint8Array) bytes = data;
    else {
      // Text this way is always a protocol error: control frames go client to
      // server, payloads come back binary.
      this.emitError({
        source: "decode",
        message: `expected a binary scan payload, got ${typeof data}`,
      });
      return;
    }

    let frames: ScreenerFrame[];
    try {
      frames = decodeScreenerFrames(bytes);
    } catch (cause) {
      // A malformed push must not kill the set — the next one may well be fine,
      // and dropping the socket would lose the good frames too.
      this.emitError({
        source: "decode",
        message: "failed to decode scan payload",
        cause,
      });
      return;
    }

    this.options.listeners?.onFrames?.(frames);
  }

  private emitError(error: TransportError): void {
    this.options.listeners?.onError?.(error);
  }

  private setStatus(status: ConnectionStatus): void {
    if (this._status === status) return;
    this._status = status;
    this.options.listeners?.onStatus?.(status);
  }
}
